const editorialSlider = {
    EditorialSlider: {
        baseStyle: () => ({
            wrapper: {
                w: '100%',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 'spacing.4',
                // Figma module top padding: 80px mobile & desktop, ~60px tablet-lg.
                pt: {base: '80px', md: '60px', lg: '80px'},
                pb: {base: '68px', md: '60px', lg: '80px'},
                position: 'relative',
                overflow: 'hidden',
                gridColumn: '1 / -1',
            },
            title: {
                textStyle: 'buttonMd',
                fontWeight: 400,
                letterSpacing: '0.2em',
                color: 'color.text.black',
                textTransform: 'uppercase',
                alignSelf: {base: 'center', md: 'flex-start', lg: 'center'},
                pl: {base: 'unset', md: 'spacing.22', lg: 'unset'},
                pb: {base: 'spacing.2'},
            },

            // Title nav: Swiper containing "View All" link + title slides.
            // On mobile: horizontally scrollable (centeredSlides). On md+: static row, centered on lg.
            titleNav: {
                w: '100%',
                overflow: 'hidden',
                pl: {base: 'unset', md: 'spacing.22', lg: 'unset'},

                '.editorial-slider-title-nav': {
                    overflow: 'visible',
                },

                '.editorial-slider-title-nav .swiper-wrapper': {
                    justifyContent: {base: 'flex-start', md: 'flex-start', lg: 'center'},
                },

                '.editorial-slider-title-nav-slide': {
                    w: 'auto',
                    cursor: 'pointer',
                    textStyle: 'paragraphLg',
                    color: 'color.text.grey.600',
                    fontWeight: 350,
                    transition: 'color 300ms ease',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                    pb: '2px',

                    '&:last-child': {
                        mr: '0 !important',
                    },

                    '&.swiper-slide-thumb-active': {
                        color: 'color.text.black',
                        fontWeight: 400,
                    },
                },

                // "View All" slide — always Light/grey, never gets thumb-active style.
                '.editorial-slider-view-all-slide': {
                    a: {
                        color: 'color.text.grey.600',
                        fontWeight: 350,
                        textDecoration: 'none',
                        transition: 'color 300ms ease',
                        _hover: {
                            color: 'color.text.black',
                            textDecoration: 'none',
                        },
                    },
                    // Prevent thumb-active from overriding the link color.
                    '&.swiper-slide-thumb-active': {
                        color: 'color.text.grey.600',
                        fontWeight: 350,
                    },
                },
            },
            swiperWrapper: {
                w: '100%',
                position: 'relative',
                overflow: 'hidden',
                h: 'auto',
                // Prevent browser "swipe to navigate back/forward" on macOS trackpad.
                overscrollBehaviorX: 'none',
                touchAction: 'pan-y pinch-zoom',
                // Tablet left padding: overflow:hidden clips at the border edge (x=0),
                // so slides in the padding area are still visible during animation.
                // Adjust the md value to match the desired visual offset.
                pl: {md: 'spacing.22', lg: 0},
                // No fixed aspectRatio: picture (position:relative, 70vw wide)
                // is in-flow; its natural height propagates up through the chain.

                '.editorial-slider-swiper': {
                    // `overflow: visible` is only needed on md+ to reveal slides sitting in the tablet
                    // left padding during the animation. On mobile (`base`) there is no such padding,
                    // and the inactive slides (width 20%) sitting off-screen extend the document's
                    // scrollable width on iOS despite the wrapper's `overflow: hidden` — producing the
                    // horizontal scroll on small devices (e.g. iPhone X). Clip on `base`, keep visible
                    // from md up.
                    overflow: {base: 'hidden', md: 'visible'},
                    w: '100%',
                    h: 'auto',
                    cursor: {md: 'grab'},
                    // Prevent browser "swipe to navigate back/forward" on macOS when
                    // horizontal scroll reaches the edge of the slider.
                    overscrollBehaviorX: 'none',
                    touchAction: 'pan-y',
                    '&:active': {
                        cursor: {md: 'grabbing'},
                    },
                },

                '.swiper-wrapper': {
                    h: 'auto',
                    alignItems: 'center',
                    // transition is driven entirely by JS (applyWrapperTranslate → setProperty !important)
                    // because Swiper speed=0 always overrides inline transitionDuration to '0ms'.
                },

                // Anti-zoom: picture is fixed-width (75vw/50vw ≈ active slide width) and
                // positioned via left:50% + transform. The slide acts as a crop window —
                // only the visible portion changes, the image never rescales.

                '.editorial-slider-slide': {
                    // inactive widths — md (tablet): 32%, lg (desktop): 24%
                    width: {base: '20%', md: '32%', lg: '24%'},
                    h: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: {base: 'none', md: 'width 500ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'},
                    opacity: {base: 1},
                },

                // Clipping window for the picture/video.
                // Mobile: portrait AR clips the in-flow image to 327:400.
                // Desktop/tablet: height is driven by the image natural height (position:relative in-flow).
                // maxH caps the crop on md+ so the description below is never pushed out of view.
                '.editorial-slider-crop': {
                    position: 'relative',
                    overflow: 'hidden',
                    w: '100%',
                    aspectRatio: {base: '327 / 400', md: 'auto'},
                    maxH: {md: '1000px'},
                },

                // Active slide — md (tablet): 75%, lg (desktop): 48%
                '.swiper-slide-active.editorial-slider-slide': {
                    width: {base: '100%', md: '75%', lg: '50%'},
                    opacity: 1,
                },

                // Focal point: --focal-x CSS custom property (set per-slide via JS from PD field).
                // picture is position:relative + left:50% (JSX). Transform handles horizontal pan.
                // No translateY needed: picture is in-flow, crop box height matches image height.
                '.editorial-slider-picture': {
                    // Mobile: left:50% + translateX(-50%) = centered (picture width = slide width).
                    // Desktop: left:50% + translateX(-focal-x) = pan to focal point.
                    transform: {base: 'translateX(-50%)', md: 'translateX(calc(-1 * var(--focal-x, 50%)))'},
                    // Same duration/easing as slide width and wrapper translate — all three
                    // must start and end together or the image appears to jump/drift.
                    transition: {base: 'none', md: 'transform 500ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'},

                    picture: {
                        width: '100%',
                        height: '100%',
                    },
                },

                '.swiper-slide-active .editorial-slider-picture': {
                    transform: 'translateX(-50%)',
                },

                // Per-slide description below the crop box, left-aligned.
                // Opacity inherited from slide (0.6 inactive → 1 active).
                '.editorial-slider-description': {
                    textStyle: 'paragraphMd',
                    color: 'color.text.black',
                    fontWeight: 400,
                    w: '100%',
                    textAlign: 'left',
                    pt: 'spacing.2',
                    flexShrink: 0,
                    minH: '35px',
                },
            },
        }),
    },
}

export default editorialSlider
